import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumb from "../DefaultComponent/BreadCrumb";
import Spinner from "../DefaultComponent/Spinner";
import {
  ActivateOrgEvaluationAction,
  AssignAccountManagerAction,
  DeactivateOrganizationAction,
  OrganizationDetailsAction,
  OrganizationPaymentDetailsAction,
  OrgMembersListAction,
  RemoveAccountManagerAction,
  OrgPricingAction,
  OrgPricingHistoryAction,
  CreateOrgPricingAction,
  UpdateOrgPricingAction,
  ClearOrgPricingAction,
  ResetOrgPricingMutationAction,
} from "../../Redux/Actions/OrganizationAction";
import { ASSIGN_ACCOUNT_MANAGER_RESET } from "../../Constants/OrganizationConstant";
import { EntityTransactionsAction } from "../../Redux/Actions/PaymentAction";
import { convertUTCToLocal, formatDate } from "../../utils/Helper";
import ManualCreditGrant from "../Payments/ManualCreditGrant/ManualCreditGrant";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

const displayValue = (value: any) =>
  value === null || value === undefined || value === "" ? "-" : value;

const buildLocation = (loc: any) => {
  if (!loc) return "-";
  const parts = [
    loc.line1,
    loc.line2,
    loc.landmark,
    loc.city,
    loc.state,
    loc.postal_code,
    loc.country,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "-";
};

const deletionImpacts = [
  "The organization will be marked as deleted and disabled.",
  "All employees, including you, will be removed from the organization.",
  "Organization permissions, roles, and group access will be permanently deleted.",
  "Users will lose access to organization resources immediately.",
  "This action may not be reversible.",
];
const deleteConfirmationPhrase = "DELETE";
const hasOrgPricingOverride = (row: any) =>
  row?.override_credit_charge !== null &&
  row?.override_credit_charge !== undefined;

const getOrgPricingRows = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.row)) return data.row;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const formatPricingCharge = (value: any) =>
  value === null || value === undefined || value === ""
    ? "-"
    : `${value} credits`;

// Org pricing override credit charge: whole or decimal, up to 2 decimals.
// Mirrors the backend (Decimal, ge=0, max_digits=10, decimal_places=2):
// at most 8 whole digits, so the max is 99,999,999.99.
const PRICING_CHARGE_PATTERN = /^\d{1,8}(\.\d{1,2})?$/;
// What may be typed so far (allows partial input like "", "2", "2.").
const PRICING_CHARGE_INPUT_PATTERN = /^\d{0,8}(\.\d{0,2})?$/;

const isValidPricingCharge = (value: string) =>
  PRICING_CHARGE_PATTERN.test(value.trim());

const isAllowedPricingChargeInput = (value: string) =>
  PRICING_CHARGE_INPUT_PATTERN.test(value);

// Only after isValidPricingCharge(): with <= 2 decimals, Number() serializes
// exactly in the JSON body ("2.05" -> 2.05).
const toPricingChargeNumber = (value: string) => Number(value.trim());

// In pricing history a null charge is a reset version: the org went back to
// the default (master) price.
const formatPricingHistoryCharge = (value: any) =>
  value === null || value === undefined || value === ""
    ? "Reset to default"
    : formatPricingCharge(value);

const formatPricingContext = (row: any) => {
  if (row?.content_type_id != null) {
    return `Content type #${row.content_type_id}`;
  }

  if (row?.external_service_resource_id != null) {
    return `External resource #${row.external_service_resource_id}`;
  }

  return "Global";
};

const formatPricingUnit = (row: any) => {
  const parts = [
    row?.unit_type,
    row?.unit,
    row?.unit_value != null ? `× ${row.unit_value}` : null,
  ].filter(Boolean);

  return parts.length ? parts.join(" · ") : "-";
};

const formatPricingDate = (value: any) => {
  if (!value) return "-";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString();
};

const OrganizationDetail: React.FC = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const orgDetailState = useSelector(
    (state: any) => state.OrganizationDetailsData
  );
  const paymentDetailState = useSelector(
    (state: any) => state.OrganizationPaymentDetailsData
  );
  const activateEvalState = useSelector(
    (state: any) => state.ActivateEvalPromoData
  );
  const deactivateOrgState = useSelector(
    (state: any) => state.DeactivateOrganizationData
  );
  const entityTransactionsState = useSelector(
    (state: any) => state.EntityTransactionsData
  );
  const orgMembersState = useSelector(
    (state: any) => state.OrgMembersListData
  );
  const assignAccountManagerState = useSelector(
    (state: any) => state.AssignAccountManagerData
  );
  const removeAccountManagerState = useSelector(
    (state: any) => state.RemoveAccountManagerData
  );
  const orgPricingState = useSelector(
    (state: any) => state.OrgPricingData
  );

  const orgPricingHistoryState = useSelector(
    (state: any) => state.OrgPricingHistoryData
  );

  const orgPricingMutationState = useSelector(
    (state: any) => state.OrgPricingMutationData
  );
  const [openEvalDialog, setOpenEvalDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openGrantDialog, setOpenGrantDialog] = useState(false);
  const [evalForm, setEvalForm] = useState({
    promo_code: "",
    org_email: "",
    org_id: Number(orgId),
  });

  const [deletionReason, setDeletionReason] = useState("");
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState("");
  const [deleteTouched, setDeleteTouched] = useState(false);
  const [deleteSubmitted, setDeleteSubmitted] = useState(false);

  const [openAddManagerDialog, setOpenAddManagerDialog] = useState(false);
  const [accountManagerEmail, setAccountManagerEmail] = useState("");
  const [accountManagerSubmitted, setAccountManagerSubmitted] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<number | null>(null);

  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
const [pricingHistoryOpen, setPricingHistoryOpen] = useState(false);
const [clearPricingDialogOpen, setClearPricingDialogOpen] = useState(false);

const [selectedPricing, setSelectedPricing] = useState<any>(null);

const [pricingSearch, setPricingSearch] = useState("");

const [pricingCharge, setPricingCharge] = useState("");
const [pricingReason, setPricingReason] = useState("");

const [clearPricingReason, setClearPricingReason] = useState("");

const [pricingFormTouched, setPricingFormTouched] = useState(false);
const [clearPricingTouched, setClearPricingTouched] = useState(false);

const [pricingFeedback, setPricingFeedback] = useState<{
  severity: "success" | "error";
  message: string;
} | null>(null);

  const handleCloseDeleteDialog = () => {
    if (deleteSubmitted && deactivateOrgState?.loading) return;
    setOpenDeleteDialog(false);
    setDeleteTouched(false);
    setDeleteSubmitted(false);
    setDeletionReason("");
    setDeleteAcknowledged(false);
    setDeleteConfirmationInput("");
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
    setDeleteTouched(false);
    setDeleteSubmitted(false);
    setDeletionReason("");
    setDeleteAcknowledged(false);
    setDeleteConfirmationInput("");
  };

  useEffect(() => {
    if (orgId) {
      dispatch(OrganizationDetailsAction(orgId));
      dispatch(OrganizationPaymentDetailsAction(orgId));
      dispatch(EntityTransactionsAction("ORGANIZATION", Number(orgId), 5));
      dispatch(OrgMembersListAction(orgId));
      dispatch(OrgPricingAction(orgId));
    }
  }, [dispatch, orgId]);

  const loading = orgDetailState?.loading;
  const data = orgDetailState?.data;
  const paymentData = paymentDetailState?.data;
  const paymentLoading = paymentDetailState?.loading;
  const membersData = orgMembersState?.data;
  const membersLoading = orgMembersState?.loading;
  const members = membersData?.row || [];

  const pricingRows = useMemo(() => {
    return getOrgPricingRows(orgPricingState?.data).sort((a, b) => {
      const groupA = String(a?.feature_group || "");
      const groupB = String(b?.feature_group || "");

      if (groupA !== groupB) {
        return groupA.localeCompare(groupB);
      }

      return String(
        a?.display_name || a?.feature_key || ""
      ).localeCompare(
        String(b?.display_name || b?.feature_key || "")
      );
    });
  }, [orgPricingState?.data]);

  const filteredPricingRows = useMemo(() => {
    const search = pricingSearch.trim().toLowerCase();

    if (!search) return pricingRows;

    return pricingRows.filter((row: any) => {
      const searchableText = [
        row?.display_name,
        row?.feature_key,
        row?.feature_group,
        row?.action_event,
        row?.content_type_id,
        row?.external_service_resource_id,
        row?.unit_type,
        row?.unit,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(search);
    });
  }, [pricingRows, pricingSearch]);
  useEffect(() => {
    if (data?.org_email) {
      setEvalForm((prev) => ({ ...prev, org_email: data.org_email }));
    }
  }, [data?.org_email]);

  useEffect(() => {
    if (activateEvalState?.status) {
      setOpenEvalDialog(false);
      dispatch(OrganizationPaymentDetailsAction(orgId || ""));
    }
  }, [activateEvalState?.status, dispatch, orgId]);

  useEffect(() => {
    if (!deleteSubmitted) return;

    if (deactivateOrgState?.status) {
      setOpenDeleteDialog(false);
      setDeleteTouched(false);
      setDeleteSubmitted(false);
      setDeletionReason("");
      setDeleteAcknowledged(false);
      setDeleteConfirmationInput("");
      navigate("/organizations");
      return;
    }

    if (deactivateOrgState?.loading === false) {
      setDeleteSubmitted(false);
    }
  }, [
    deactivateOrgState?.status,
    deactivateOrgState?.loading,
    deleteSubmitted,
    dispatch,
    navigate,
    orgId,
  ]);

  useEffect(() => {
    if (!accountManagerSubmitted) return;
    if (assignAccountManagerState?.status) {
      setOpenAddManagerDialog(false);
      setAccountManagerEmail("");
      setAccountManagerSubmitted(false);
      if (orgId) {
        dispatch(OrgMembersListAction(orgId));
        dispatch(OrganizationDetailsAction(orgId));
      }
      return;
    }
    if (assignAccountManagerState?.loading === false) {
      setAccountManagerSubmitted(false);
    }
  }, [
    assignAccountManagerState?.status,
    assignAccountManagerState?.loading,
    accountManagerSubmitted,
    dispatch,
    orgId,
  ]);

  useEffect(() => {
    if (removingMemberId === null) return;
    if (removeAccountManagerState?.status) {
      setRemovingMemberId(null);
      if (orgId) {
        dispatch(OrgMembersListAction(orgId));
        dispatch(OrganizationDetailsAction(orgId));
      }
      return;
    }
    if (removeAccountManagerState?.loading === false) {
      setRemovingMemberId(null);
    }
  }, [
    removeAccountManagerState?.status,
    removeAccountManagerState?.loading,
    removingMemberId,
    dispatch,
    orgId,
  ]);

  const renderInfoRow = (label: string, value: any) => (
    <div className="org-detail__info-row">
      <span className="org-detail__info-label">{label}</span>
      <span className="org-detail__info-value">{displayValue(value)}</span>
    </div>
  );

  const trimmedReason = deletionReason.trim();
  const reasonTooShort = trimmedReason.length > 0 && trimmedReason.length < 5;
  const reasonTooLong = trimmedReason.length > 1500;
  const reasonIsValid =
    trimmedReason.length >= 5 && trimmedReason.length <= 1500;
  const showReasonError = deleteTouched && !reasonIsValid;
  const isDeleteSubmitting = deleteSubmitted && deactivateOrgState?.loading;
  const deleteErrorMessage =
    deactivateOrgState?.data?.message || deactivateOrgState?.error || "";
  const confirmationMatches =
    deleteConfirmationInput.trim() === deleteConfirmationPhrase;
  const showConfirmationError =
    deleteTouched && deleteConfirmationInput.trim().length > 0 && !confirmationMatches;
  const showAcknowledgeError = deleteTouched && !deleteAcknowledged;

  const reasonHelperText = showReasonError
    ? trimmedReason.length === 0
      ? "Reason is required."
      : reasonTooShort
      ? "Reason must be at least 5 characters."
      : reasonTooLong
      ? "Reason must be at most 1500 characters."
      : ""
    : `${trimmedReason.length}/1500`;

  const handleDeleteOrg = async () => {
    setDeleteTouched(true);
    if (!orgId) return;
    if (!reasonIsValid) return;
    setDeleteSubmitted(true);
    await dispatch(
      DeactivateOrganizationAction({
        orgId,
        deletion_reason: trimmedReason,
      })
    );
  };

  const accountManagerEmailValid =
    /^[^\s@]+@(skillbrew\.com|skillbrew\.ai)$/i.test(
      accountManagerEmail.trim()
    );

  const handleAddAccountManager = async () => {
    if (!orgId || !accountManagerEmailValid) return;
    setAccountManagerSubmitted(true);
    await dispatch(
      AssignAccountManagerAction({
        orgId,
        email: accountManagerEmail.trim(),
      })
    );
  };

  const handleRemoveAccountManager = async (orgMemberId: number) => {
    if (!orgId) return;
    if (!window.confirm("Remove this Account Manager from the organization?"))
      return;
    setRemovingMemberId(orgMemberId);
    await dispatch(RemoveAccountManagerAction({ orgId, orgMemberId }));
  };
  // Whole or decimal credits, up to 2 decimal places (matches the backend).
  const pricingChargeValid = isValidPricingCharge(pricingCharge);

  const pricingReasonValid =
    pricingReason.trim().length > 0;

  const pricingFormValid =
    pricingChargeValid && pricingReasonValid;

  const clearPricingReasonValid =
    clearPricingReason.trim().length > 0;

  const pricingMutationLoading =
    orgPricingMutationState?.loading === true;
  const renderPaymentSection = useMemo(() => {
    if (paymentLoading) return <Spinner />;
    const balance = paymentData?.balance;
    const activePlan = paymentData?.active_plan;
    const evaluation = paymentData?.evaluation_period;
    const isEvalActive = balance?.is_evaluation_period;
    const isFreezed = balance?.is_freezed;

    const evalButtonLabel = isFreezed
      ? "Reactivate Evaluation"
      : isEvalActive
      ? "Extend Evaluation"
      : "Activate Evaluation";

    return (
      <Card className="org-detail__card">
        <div className="org-detail__section-header">
          <div>
            <p className="org-detail__eyebrow">Billing</p>
            <h5 className="mb-0">Payment Details</h5>
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenEvalDialog(true)}
          >
            {evalButtonLabel}
          </Button>
        </div>
        <Grid container spacing={3} className="mt-1">
          <Grid item xs={12} md={4}>
            <div className="org-detail__mini-card">
              <p className="org-detail__eyebrow">Balance</p>
              {renderInfoRow("Total", balance?.total)}
              {renderInfoRow("Plan", balance?.plan)}
              {renderInfoRow("Free", balance?.free)}
              {renderInfoRow(
                "Account Status",
                balance?.is_freezed ? "Freezed" : "Active"
              )}
              {renderInfoRow(
                "Plan Expires",
                balance?.expiration_date
                  ? convertUTCToLocal(balance.expiration_date)
                  : "-"
              )}
              {renderInfoRow(
                "Eval Expires",
                balance?.evaluation_expiry_date || "-"
              )}
            </div>
          </Grid>
          <Grid item xs={12} md={4}>
            <div className="org-detail__mini-card">
              <p className="org-detail__eyebrow">
                {isEvalActive ? "Evaluation" : "Active Plan"}
              </p>
              {isEvalActive ? (
                <>
                  {renderInfoRow("Promo", evaluation?.promo_code)}
                  {renderInfoRow("Credits", evaluation?.credits_awarded)}
                  {renderInfoRow("Valid Days", evaluation?.valid_duration_days)}
                  {renderInfoRow(
                    "Used At",
                    evaluation?.used_at
                      ? convertUTCToLocal(evaluation.used_at)
                      : "-"
                  )}
                  {renderInfoRow(
                    "Evaluation Expiration",
                    evaluation?.evaluation_expiration
                      ? convertUTCToLocal(evaluation.evaluation_expiration)
                      : "-"
                  )}
                </>
              ) : activePlan ? (
                <>
                  {renderInfoRow("Plan", activePlan?.plan_name)}
                  {renderInfoRow("Category", activePlan?.plan_category)}
                  {renderInfoRow("Type", activePlan?.plan_type)}
                  {renderInfoRow("Status", activePlan?.status)}
                  {renderInfoRow("Credits", activePlan?.credits)}
                  {renderInfoRow(
                    "Duration",
                    activePlan?.duration
                      ? `${activePlan.duration} ${activePlan.duration_type || ""}`
                      : "-"
                  )}
                  {renderInfoRow(
                    "Start",
                    activePlan?.start_date
                      ? convertUTCToLocal(activePlan.start_date)
                      : "-"
                  )}
                  {renderInfoRow(
                    "End",
                    activePlan?.end_date
                      ? convertUTCToLocal(activePlan.end_date)
                      : "-"
                  )}
                </>
              ) : (
                <p>No active plan.</p>
              )}
            </div>
          </Grid>
          <Grid item xs={12} md={4}>
            <div className="org-detail__mini-card">
              <p className="org-detail__eyebrow">Status</p>
              <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                <Chip
                  label={
                    balance?.is_evaluation_period
                      ? "Evaluation Active"
                      : "Eval Inactive"
                  }
                  color={balance?.is_evaluation_period ? "success" : "default"}
                />
                <Chip
                  label={balance?.is_freezed ? "Freezed" : "Not Freezed"}
                  color={balance?.is_freezed ? "error" : "primary"}
                />
                <Chip
                  label={`Total Members: ${displayValue(data?.total_members)}`}
                  variant="outlined"
                />
                <Chip
                  label={`Total Tests: ${displayValue(data?.total_tests)}`}
                  variant="outlined"
                />
              </Stack>
            </div>
          </Grid>
        </Grid>
      </Card>
    );
  }, [paymentData, paymentLoading, data?.total_members, data?.total_tests]);
  const resetPricingForm = () => {
    setSelectedPricing(null);
    setPricingCharge("");
    setPricingReason("");
    setPricingFormTouched(false);
    dispatch(ResetOrgPricingMutationAction());
  };

  const handleOpenPricingDialog = (row: any) => {
    setSelectedPricing(row);

    setPricingCharge(
      row?.override_credit_charge != null
        ? String(row.override_credit_charge)
        : ""
    );

    setPricingReason("");
    setPricingFormTouched(false);
    setPricingFeedback(null);

    dispatch(ResetOrgPricingMutationAction());

    setPricingDialogOpen(true);
  };

  const handleClosePricingDialog = () => {
    if (orgPricingMutationState?.loading) return;

    setPricingDialogOpen(false);
    resetPricingForm();
  };

  const handleOpenClearPricingDialog = (row: any) => {
    setSelectedPricing(row);
    setClearPricingReason("");
    setClearPricingTouched(false);
    setPricingFeedback(null);

    dispatch(ResetOrgPricingMutationAction());

    setClearPricingDialogOpen(true);
  };

  const handleCloseClearPricingDialog = () => {
    if (orgPricingMutationState?.loading) return;

    setClearPricingDialogOpen(false);
    setSelectedPricing(null);
    setClearPricingReason("");
    setClearPricingTouched(false);

    dispatch(ResetOrgPricingMutationAction());
  };

  const handleOpenPricingHistory = () => {
    setPricingHistoryOpen(true);

    if (orgId) {
      dispatch(OrgPricingHistoryAction(orgId));
    }
  };

  const handleSavePricing = async () => {
    setPricingFormTouched(true);

    if (!orgId || !selectedPricing) return;

    if (!isValidPricingCharge(pricingCharge)) {
      return;
    }

    const charge = toPricingChargeNumber(pricingCharge);
    const reason = pricingReason.trim();

    if (!reason) {
      return;
    }

    let response;

    if (hasOrgPricingOverride(selectedPricing)) {
      const overrideId =
        selectedPricing?.override_id ?? selectedPricing?.id;

      if (!overrideId) {
        setPricingFeedback({
          severity: "error",
          message: "Unable to identify the pricing override.",
        });

        return;
      }

      response = await dispatch(
        UpdateOrgPricingAction({
          overrideId: Number(overrideId),
          credit_charge: charge,
          reason,
        })
      );
    } else {
      response = await dispatch(
        CreateOrgPricingAction({
          org_id: Number(orgId),
          feature_id: Number(selectedPricing.feature_id),
          action_event: selectedPricing.action_event,
          credit_charge: charge,
          content_type_id:
            selectedPricing.content_type_id ?? null,
          external_service_resource_id:
            selectedPricing.external_service_resource_id ?? null,
          reason,
        })
      );
    }

    if (response?.status === true) {
      setPricingFeedback({
        severity: "success",
        message: hasOrgPricingOverride(selectedPricing)
          ? "Pricing override updated successfully."
          : "Pricing override created successfully.",
      });

      setPricingDialogOpen(false);
      resetPricingForm();

      dispatch(OrgPricingAction(orgId));

      if (pricingHistoryOpen) {
        dispatch(OrgPricingHistoryAction(orgId));
      }
    } else if (response) {
      setPricingFeedback({
        severity: "error",
        message:
          response?.message ||
          "Failed to save pricing override.",
      });
    }
  };

  const handleClearPricing = async () => {
    setClearPricingTouched(true);

    if (!selectedPricing || !orgId) return;

    const reason = clearPricingReason.trim();

    if (!reason) return;

    const overrideId =
      selectedPricing?.override_id ??
      selectedPricing?.id;

    if (!overrideId) {
      setPricingFeedback({
        severity: "error",
        message: "Unable to identify the pricing override.",
      });

      return;
    }

    const response = await dispatch(
      ClearOrgPricingAction({
        overrideId: Number(overrideId),
        reason,
      })
    );

    if (response?.status === true) {
      setPricingFeedback({
        severity: "success",
        message: "Pricing override cleared successfully.",
      });

      setClearPricingDialogOpen(false);
      setSelectedPricing(null);
      setClearPricingReason("");
      setClearPricingTouched(false);

      dispatch(ResetOrgPricingMutationAction());

      dispatch(OrgPricingAction(orgId));

      if (pricingHistoryOpen) {
        dispatch(OrgPricingHistoryAction(orgId));
      }
    } else if (response) {
      setPricingFeedback({
        severity: "error",
        message:
          response?.message ||
          "Failed to clear pricing override.",
      });
    }
  };
  return (
    <>
      {(loading || paymentLoading) && <Spinner />}
      <div className="page-wrapper card org-detail-page">
        <div className="col-md-12 d-flex justify-content-between align-items-center">
          <Breadcrumb
            pageTitle="Organization Details"
            breadcrumbs={[
              { label: "Organizations", path: "/organizations", isActive: false },
              { label: "Details", path: "", isActive: true },
            ]}
          />
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>

        <div className="org-detail__content">
          <div className="org-detail__hero card shadow-sm">
            <div className="org-detail__hero-body">
              {data?.logo && (
                <div className="org-detail__logo-wrap">
                  <img src={data.logo} alt="Logo" className="org-detail__logo" />
                </div>
              )}
              <div>
                <p className="org-detail__eyebrow">Organization</p>
                <h3 className="mb-1">{displayValue(data?.org_name)}</h3>
                <div className="text-muted">{displayValue(data?.org_domain)}</div>
                <div className="text-muted">
                  Joined: {displayValue(formatDate(data?.created_at))}
                </div>
              </div>
            </div>
          </div>

          <div className="org-detail__grid">
            <Card className="org-detail__card">
              <div className="org-detail__section-header">
                <div>
                  <p className="org-detail__eyebrow">Profile</p>
                  <h5 className="mb-0">About</h5>
                </div>
              </div>
              <div className="org-detail__info-grid">
                {renderInfoRow("Email", data?.org_email)}
                {renderInfoRow("Admin Email", data?.admin_mail)}
                {renderInfoRow("Job Title", data?.job_title)}
                {renderInfoRow("Contact", data?.contact_no)}
                {renderInfoRow("Website", data?.website_url)}
                {renderInfoRow("Type", data?.org_type)}
                {renderInfoRow(
                  "Size",
                  data?.size_min || data?.size_max
                    ? `${displayValue(data?.size_min)} - ${displayValue(
                        data?.size_max
                      )}`
                    : "-"
                )}
                {renderInfoRow("Country", data?.country)}
                {renderInfoRow("Location", buildLocation(data?.location))}
              </div>
              <div className="mt-3">
                <p className="org-detail__eyebrow">Description</p>
                <div className="org-detail__description">
                  {displayValue(data?.description)}
                </div>
              </div>
            </Card>

            <Card className="org-detail__card">
              <div className="org-detail__section-header">
                <div>
                  <p className="org-detail__eyebrow">Health</p>
                  <h5 className="mb-0">Stats</h5>
                </div>
              </div>
              <div className="org-detail__info-grid">
                {renderInfoRow("Total Tests", data?.total_tests)}
                {renderInfoRow("Total Batches", data?.total_batches)}
                {renderInfoRow("Total Members", data?.total_members)}
                {renderInfoRow(
                  "Verified",
                  data?.is_verified ? "Yes" : "No"
                )}
                {renderInfoRow(
                  "Domain Verified",
                  data?.is_domain_verified ? "Yes" : "No"
                )}
                {renderInfoRow("Verified By", data?.verified_by?.email)}
                {renderInfoRow(
                  "Domain Verified By",
                  data?.domain_verfied_by?.email
                )}
                {renderInfoRow("Created By", data?.created_by?.email)}
              </div>
            </Card>
          </div>

          <div className="org-detail__grid">
            <Card className="org-detail__card">
              <div className="org-detail__section-header">
                <div>
                  <p className="org-detail__eyebrow">Team</p>
                  <h5 className="mb-0">Members</h5>
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenAddManagerDialog(true)}
                >
                  Add Account Manager
                </Button>
              </div>
              {membersLoading ? (
                <Spinner />
              ) : orgMembersState?.error ? (
                <p className="text-muted mt-2 mb-0">
                  Failed to load members. {orgMembersState.error}
                </p>
              ) : members.length === 0 ? (
                <p className="text-muted mt-2 mb-0">No members yet.</p>
              ) : (
                <Stack spacing={1.5} className="mt-2">
                  {members.map((member: any) => (
                    <div
                      key={member.id}
                      className="org-detail__info-row"
                      style={{ alignItems: "center" }}
                    >
                      <div>
                        <div>{displayValue(member.fullname)}</div>
                        <div className="text-muted">
                          {displayValue(member.email)}
                        </div>
                      </div>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={
                            member.is_account_manager
                              ? "SkillBrew Account Manager"
                              : displayValue(member.org_member_type)
                          }
                          color={
                            member.is_account_manager ? "success" : "default"
                          }
                          variant={
                            member.is_account_manager ? "filled" : "outlined"
                          }
                        />
                        {member.is_account_manager && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={removingMemberId === member.id}
                            onClick={() => handleRemoveAccountManager(member.id)}
                          >
                            {removingMemberId === member.id
                              ? "Removing..."
                              : "Remove"}
                          </Button>
                        )}
                      </Stack>
                    </div>
                  ))}
                </Stack>
              )}
            </Card>
          </div>

          <div className="org-detail__grid">{renderPaymentSection}</div>
          <div className="org-detail__grid">
            <Card className="org-detail__card">
              <div className="org-detail__section-header">
                <div>
                  <p className="org-detail__eyebrow">Billing</p>
                  <h5 className="mb-0">Organization Pricing</h5>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Customize credit deductions for this organization.
                    Blank overrides use the default pricing.
                  </Typography>
                </div>

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenPricingHistory}
                  >
                    Pricing History
                  </Button>
                </Stack>
              </div>

              {pricingFeedback && (
                <Alert
                  severity={pricingFeedback.severity}
                  sx={{ mb: 2 }}
                  onClose={() => setPricingFeedback(null)}
                >
                  {pricingFeedback.message}
                </Alert>
              )}

              <Box sx={{ px: 2, pb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search pricing"
                  placeholder="Search feature, action, group or context..."
                  value={pricingSearch}
                  onChange={(e) => setPricingSearch(e.target.value)}
                  sx={{ mb: 2 }}
                />

                {orgPricingState?.loading ? (
                  <Box sx={{ py: 3 }}>
                    <Spinner />
                  </Box>
                ) : orgPricingState?.error ? (
                  <Alert severity="error">
                    Failed to load organization pricing.
                    {" "}
                    {orgPricingState.error}
                  </Alert>
                ) : filteredPricingRows.length === 0 ? (
                  <Box sx={{ py: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {pricingRows.length === 0
                        ? "No pricing configuration found."
                        : "No pricing rows match your search."}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Feature</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Action</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Context</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Default</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Override</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Effective</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Unit</strong>
                          </TableCell>

                          <TableCell>
                            <strong>Version</strong>
                          </TableCell>

                          <TableCell align="right">
                            <strong>Actions</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {filteredPricingRows.map((row: any, index: number) => {
                          const hasOverride =
                            hasOrgPricingOverride(row);

                          return (
                            <TableRow
                              key={
                                row?.id ??
                                `${row?.feature_id}-${row?.action_event}-${row?.content_type_id ?? "global"}-${row?.external_service_resource_id ?? "global"}-${index}`
                              }
                              hover
                            >
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                >
                                  {displayValue(
                                    row?.display_name ||
                                      row?.feature_key
                                  )}
                                </Typography>

                                {row?.feature_key &&
                                  row?.display_name !==
                                    row?.feature_key && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {row.feature_key}
                                    </Typography>
                                  )}

                                {row?.feature_group && (
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                  >
                                    {row.feature_group}
                                  </Typography>
                                )}
                              </TableCell>

                              <TableCell>
                                <Chip
                                  size="small"
                                  label={
                                    displayValue(
                                      row?.action_event
                                    )
                                  }
                                  variant="outlined"
                                />
                              </TableCell>

                              <TableCell>
                                <Typography variant="body2">
                                  {formatPricingContext(row)}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                {formatPricingCharge(
                                  row?.default_credit_charge
                                )}
                              </TableCell>

                              <TableCell>
                                {hasOverride ? (
                                  <Chip
                                    size="small"
                                    color="primary"
                                    label={formatPricingCharge(
                                      row?.override_credit_charge
                                    )}
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    No override
                                  </Typography>
                                )}
                              </TableCell>

                              <TableCell>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                >
                                  {formatPricingCharge(
                                    row?.effective_credit_charge
                                  )}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                {formatPricingUnit(row)}
                              </TableCell>

                              <TableCell>
                                {hasOverride ? (
                                  <Chip
                                    size="small"
                                    label={`v${row?.override_version ?? "-"}`}
                                  />
                                ) : (
                                  "-"
                                )}
                              </TableCell>

                              <TableCell align="right">
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  justifyContent="flex-end"
                                >
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      handleOpenPricingDialog(row)
                                    }
                                  >
                                    {hasOverride
                                      ? "Edit"
                                      : "Set Override"}
                                  </Button>

                                  {hasOverride && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={() =>
                                        handleOpenClearPricingDialog(
                                          row
                                        )
                                      }
                                    >
                                      Clear
                                    </Button>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Card>
          </div>
          <div className="org-detail__grid">
            <Card className="org-detail__card">
              <div className="org-detail__section-header">
                <p className="org-detail__eyebrow">Billing</p>
                <h5 className="mb-0">Recent Transactions</h5>
              </div>

              <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pb: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => setOpenGrantDialog(true)}
                >
                  Create Transaction
                </Button>
              </Box>

              {entityTransactionsState?.loading ? (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Spinner />
                </Box>
              ) : entityTransactionsState?.data?.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Transaction ID</strong></TableCell>
                        <TableCell><strong>Plan</strong></TableCell>
                        <TableCell><strong>Credits</strong></TableCell>
                        <TableCell><strong>Bonus</strong></TableCell>
                        <TableCell><strong>Amount</strong></TableCell>
                        <TableCell><strong>Source</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Reason</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entityTransactionsState.data.map((tx: any) => (
                        <TableRow key={tx.id}>
                          <TableCell>{tx.transaction_id || "-"}</TableCell>
                          <TableCell>{tx.plan_name || "-"}</TableCell>
                          <TableCell>{displayValue(tx.total_credits)}</TableCell>
                          <TableCell>{displayValue(tx.bonus_credits)}</TableCell>
                          <TableCell>
                            {tx.amount_to_pay != null && tx.currency
                              ? `${tx.currency} ${tx.amount_to_pay}`
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {displayValue(
                              tx.payment_source === "MANUAL"
                                ? "Manual"
                                : tx.payment_source
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tx.status}
                              size="small"
                              color={tx.status === "SUCCESS" || tx.status === "COMPLETED" ? "success" : tx.status === "FAILED" ? "error" : "default"}
                            />
                          </TableCell>
                          <TableCell>
                            {tx.credit_reason
                              ? tx.credit_reason
                              : tx.payment_source === "MANUAL"
                              ? tx.plan_name || "-"
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {tx.created_at ? formatDate(tx.created_at) : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No transactions found for this organization.
                  </Typography>
                </Box>
              )}
            </Card>
          </div>

          <div className="org-detail__grid">
            <Card
              className="org-detail__card org-detail__danger-card"
              sx={{ borderColor: "error.main" }}
            >

              <div className="org-detail__danger-note org-detail__danger-note--centered">
                <div className="org-detail__danger-note-icon">!</div>
                <div>
                  <Typography
                    variant="subtitle2"
                    className="org-detail__danger-note-title"
                  >
                    <h4><strong>Read Before You Continue</strong></h4>
                  </Typography>
                  <Typography variant="body2" className="org-detail__danger-note-text">
                    Deleting an organization is a destructive action. Review the
                    impact below and continue only if you are fully sure.
                  </Typography>
                </div>
              </div>

              <div className="org-detail__danger-list">
                {deletionImpacts.map((impact) => (
                  <div key={impact} className="org-detail__danger-item">
                    <span className="org-detail__danger-dot" />
                    <Typography variant="body2">{impact}</Typography>
                  </div>
                ))}
              </div>

              <div className="org-detail__danger-footer">
                <Typography variant="caption" className="org-detail__danger-footer-text">
                  Confirm the impact above before continuing with this permanent
                  action.
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleOpenDeleteDialog}
                >
                  Delete Organization
                </Button>
              </div>
            </Card>
          </div>

          <div className="org-detail__grid">
            {data?.cover_img && (
              <Card className="org-detail__card org-detail__cover">
                <div className="org-detail__section-header">
                  <p className="org-detail__eyebrow">Media</p>
                  <h5 className="mb-0">Cover Image</h5>
                </div>
                <div className="org-detail__cover-img-wrap">
                  <img
                    src={data.cover_img}
                    alt="Cover"
                    className="org-detail__cover-img"
                  />
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      <Dialog open={openEvalDialog} onClose={() => setOpenEvalDialog(false)}>
        <DialogTitle>Activate Evaluation Period</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 360 }}>
            <TextField
              label="Promo Code"
              value={evalForm.promo_code}
              onChange={(e) =>
                setEvalForm((prev) => ({ ...prev, promo_code: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Org Email"
              value={evalForm.org_email}
              onChange={(e) =>
                setEvalForm((prev) => ({ ...prev, org_email: e.target.value }))
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEvalDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() =>
              dispatch(
                ActivateOrgEvaluationAction({
                  ...evalForm,
                  org_id: Number(orgId),
                })
              )
            }
            variant="contained"
            disabled={!evalForm.promo_code || !evalForm.org_email}
          >
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddManagerDialog}
        onClose={() => {
          setOpenAddManagerDialog(false);
          setAccountManagerEmail("");
          dispatch({ type: ASSIGN_ACCOUNT_MANAGER_RESET });
        }}
      >
        <DialogTitle>Add Account Manager</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 360 }}>
            <Typography variant="body2" color="text.secondary">
              Enter the SkillBrew email of the staff member to assign as this
              organization's Account Manager. They'll get full access to this
              organization, without going through the normal invite flow.
            </Typography>
            <TextField
              label="Staff Email"
              value={accountManagerEmail}
              onChange={(e) => setAccountManagerEmail(e.target.value)}
              fullWidth
              autoFocus
              error={
                accountManagerEmail.length > 0 && !accountManagerEmailValid
              }
              helperText={
                accountManagerEmail.length > 0 && !accountManagerEmailValid
                  ? "Enter a valid @skillbrew.com or @skillbrew.ai email address."
                  : ""
              }
            />
            {assignAccountManagerState?.error && (
              <Alert severity="error">{assignAccountManagerState.error}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenAddManagerDialog(false);
              setAccountManagerEmail("");
              dispatch({ type: ASSIGN_ACCOUNT_MANAGER_RESET });
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddAccountManager}
            variant="contained"
            disabled={
              !accountManagerEmailValid || assignAccountManagerState?.loading
            }
          >
            {assignAccountManagerState?.loading ? "Adding..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={pricingDialogOpen}
        onClose={handleClosePricingDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {hasOrgPricingOverride(selectedPricing)
            ? "Edit Pricing Override"
            : "Set Pricing Override"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={600}
              >
                {displayValue(
                  selectedPricing?.display_name ||
                    selectedPricing?.feature_key
                )}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Action:{" "}
                {displayValue(
                  selectedPricing?.action_event
                )}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Context:{" "}
                {formatPricingContext(selectedPricing)}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Default:{" "}
                {formatPricingCharge(
                  selectedPricing?.default_credit_charge
                )}
              </Typography>
            </Box>

            <TextField
              label="Credit Charge"
              // Text + decimal keypad: type="number" mangles partial input
              // like "2." and accepts "1e5".
              type="text"
              value={pricingCharge}
              onChange={(e) => {
                const next = e.target.value;
                if (isAllowedPricingChargeInput(next)) {
                  setPricingCharge(next);
                }
              }}
              onBlur={() => setPricingFormTouched(true)}
              fullWidth
              required
              inputProps={{
                inputMode: "decimal",
              }}
              error={
                pricingFormTouched &&
                !pricingChargeValid
              }
              helperText={
                pricingFormTouched &&
                !pricingChargeValid
                  ? "Enter a number from 0 to 99999999.99 with up to 2 decimal places."
                  : "Credits deducted for new usage. Up to 2 decimal places (e.g. 2.25)."
              }
              disabled={pricingMutationLoading}
            />

            <TextField
              label="Reason"
              value={pricingReason}
              onChange={(e) =>
                setPricingReason(e.target.value)
              }
              onBlur={() => setPricingFormTouched(true)}
              fullWidth
              required
              multiline
              minRows={3}
              placeholder="Explain why this pricing override is being applied."
              error={
                pricingFormTouched &&
                !pricingReasonValid
              }
              helperText={
                pricingFormTouched &&
                !pricingReasonValid
                  ? "Reason is required."
                  : "Required for pricing audit history."
              }
              disabled={pricingMutationLoading}
            />

            {orgPricingMutationState?.error && (
              <Alert severity="error">
                {orgPricingMutationState.error}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClosePricingDialog}
            variant="outlined"
            disabled={pricingMutationLoading}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSavePricing}
            variant="contained"
            disabled={
              !pricingFormValid ||
              pricingMutationLoading
            }
          >
            {pricingMutationLoading
              ? "Saving..."
              : hasOrgPricingOverride(selectedPricing)
              ? "Save Changes"
              : "Set Override"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={clearPricingDialogOpen}
        onClose={handleCloseClearPricingDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Clear Pricing Override
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Clearing this override will make new usage fall
              back to the default pricing.
            </Alert>

            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
              >
                {displayValue(
                  selectedPricing?.display_name ||
                    selectedPricing?.feature_key
                )}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Current override:{" "}
                {formatPricingCharge(
                  selectedPricing?.override_credit_charge
                )}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Default:{" "}
                {formatPricingCharge(
                  selectedPricing?.default_credit_charge
                )}
              </Typography>
            </Box>

            <TextField
              label="Reason"
              value={clearPricingReason}
              onChange={(e) =>
                setClearPricingReason(e.target.value)
              }
              onBlur={() => setClearPricingTouched(true)}
              fullWidth
              required
              multiline
              minRows={3}
              placeholder="Explain why this pricing override is being cleared."
              error={
                clearPricingTouched &&
                !clearPricingReasonValid
              }
              helperText={
                clearPricingTouched &&
                !clearPricingReasonValid
                  ? "Reason is required."
                  : "Required for pricing audit history."
              }
              disabled={pricingMutationLoading}
            />

            {orgPricingMutationState?.error && (
              <Alert severity="error">
                {orgPricingMutationState.error}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseClearPricingDialog}
            variant="outlined"
            disabled={pricingMutationLoading}
          >
            Cancel
          </Button>

          <Button
            onClick={handleClearPricing}
            variant="contained"
            color="error"
            disabled={
              !clearPricingReasonValid ||
              pricingMutationLoading
            }
          >
            {pricingMutationLoading
              ? "Clearing..."
              : "Clear Override"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={pricingHistoryOpen}
        onClose={() => setPricingHistoryOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Organization Pricing History
        </DialogTitle>

        <DialogContent>
          {orgPricingHistoryState?.loading ? (
            <Box sx={{ py: 4 }}>
              <Spinner />
            </Box>
          ) : orgPricingHistoryState?.error ? (
            <Alert severity="error">
              Failed to load pricing history.
              {" "}
              {orgPricingHistoryState.error}
            </Alert>
          ) : (
            (() => {
              const historyRows = getOrgPricingRows(
                orgPricingHistoryState?.data
              );

              if (historyRows.length === 0) {
                return (
                  <Box sx={{ py: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      No pricing history found for this organization.
                    </Typography>
                  </Box>
                );
              }

              return (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Version</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Feature</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Action</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Context</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Charge</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Reason</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Changed By</strong>
                        </TableCell>

                        <TableCell>
                          <strong>Date</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {historyRows.map(
                        (history: any, index: number) => (
                          <TableRow
                            key={
                              history?.id ??
                              `${history?.override_id}-${history?.override_version}-${index}`
                            }
                          >
                            <TableCell>
                              <Chip
                                size="small"
                                label={`v${
                                  history?.override_version ??
                                  history?.version ??
                                  "-"
                                }`}
                              />
                            </TableCell>

                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                              >
                                {displayValue(
                                  history?.display_name ||
                                    history?.feature_name ||
                                    history?.feature_key
                                )}
                              </Typography>

                              {history?.feature_key && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {history.feature_key}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              {displayValue(
                                history?.action_event
                              )}
                            </TableCell>

                            <TableCell>
                              {formatPricingContext(history)}
                            </TableCell>

                            <TableCell>
                              {formatPricingHistoryCharge(
                                history?.credit_charge ??
                                  history?.override_credit_charge
                              )}
                            </TableCell>

                            <TableCell>
                              {displayValue(
                                history?.reason
                              )}
                            </TableCell>

                            <TableCell>
                              {displayValue(
                                history?.changed_by?.email ||
                                  history?.changed_by_email ||
                                  history?.created_by?.email ||
                                  history?.created_by
                              )}
                            </TableCell>

                            <TableCell>
                              {formatPricingDate(
                                history?.created_at ||
                                  history?.changed_at ||
                                  history?.updated_at
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setPricingHistoryOpen(false)}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert
              severity="warning"
              variant="filled"
              className="org-detail__dialog-alert"
            >
              This will deactivate <strong>{displayValue(data?.org_name)}</strong>
              {" "}and remove access for its members immediately.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Please provide a reason for deletion. This is required for audit
              purposes before we submit the request.
            </Typography>
            {deleteErrorMessage && !isDeleteSubmitting && (
              <Alert severity="error">{deleteErrorMessage}</Alert>
            )}
            <TextField
              label="Reason"
              value={deletionReason}
              onChange={(e) => setDeletionReason(e.target.value)}
              onBlur={() => setDeleteTouched(true)}
              placeholder="Add a brief explanation for why this organization is being deleted"
              fullWidth
              required
              multiline
              minRows={4}
              error={showReasonError}
              helperText={reasonHelperText}
              inputProps={{ maxLength: 1500 }}
              disabled={isDeleteSubmitting}
              autoFocus
            />
            <div
              className={`org-detail__confirm-panel ${
                deleteAcknowledged ? "org-detail__confirm-panel--active" : ""
              }`}
            >
              <FormControlLabel
                className="org-detail__confirm-check"
                control={
                  <Checkbox
                    checked={deleteAcknowledged}
                    onChange={(e) => setDeleteAcknowledged(e.target.checked)}
                    color="error"
                    disabled={isDeleteSubmitting}
                  />
                }
                label={
                  <span className="org-detail__confirm-check-label">
                    I understand this action is permanent and will remove
                    organization access for all members.
                  </span>
                }
              />
              {showAcknowledgeError && (
                <Typography
                  variant="caption"
                  color="error"
                  className="org-detail__confirm-error"
                >
                  Please confirm that you understand the impact before deleting.
                </Typography>
              )}

              {deleteAcknowledged && (
                <div className="org-detail__confirm-type-wrap">
                  <Typography
                    variant="body2"
                    className="org-detail__confirm-type-label"
                  >
                    Final confirmation: type{" "}
                    <strong>{deleteConfirmationPhrase}</strong> to enable the
                    delete button. Use capital letters exactly as shown.
                  </Typography>
                  <TextField
                    label={`Type ${deleteConfirmationPhrase} in uppercase`}
                    value={deleteConfirmationInput}
                    onChange={(e) => setDeleteConfirmationInput(e.target.value)}
                    onBlur={() => setDeleteTouched(true)}
                    fullWidth
                    error={showConfirmationError}
                    helperText={
                      showConfirmationError
                        ? `Please type ${deleteConfirmationPhrase} exactly in uppercase to continue.`
                        : `Enter ${deleteConfirmationPhrase} exactly as shown.`
                    }
                    disabled={isDeleteSubmitting}
                  />
                </div>
              )}
            </div>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            disabled={isDeleteSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteOrg}
            variant="contained"
            color="error"
            disabled={
              !reasonIsValid ||
              !deleteAcknowledged ||
              !confirmationMatches ||
              isDeleteSubmitting
            }
          >
            {isDeleteSubmitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <ManualCreditGrant
        open={openGrantDialog}
        onClose={() => {
          setOpenGrantDialog(false);
          if (orgId) {
            dispatch(EntityTransactionsAction("ORGANIZATION", Number(orgId), 5));
          }
        }}
        entityType="ORGANIZATION"
        entityId={Number(orgId)}
        entityName={data?.org_name}
        entityEmail={data?.org_email}
      />
    </>
  );
};

export default OrganizationDetail;
